import { ConnectedPosition } from '@angular/cdk/overlay';
import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  Inject,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LogEvent } from '@core/enums/log-event';
import { LoggerService } from '@core/services/logger.service';
import { environment } from '@env';
import { Company, Translator, TRANSLATOR } from '@livechat/shared';
import {
  AskDropdownDirective,
  ModalService,
  ToastService,
} from 'asksuite-citrus';
import { filter, take } from 'rxjs/operators';
import { AnswerTemplateDto } from '../../domain/dtos';
import { AnswerTemplatesFacade } from '../../services/answer-templates.facade';
import {
  AnswerTemplateDeleteModalCloseData,
  AnswerTemplateDeleteModalComponent,
  AnswerTemplateDeleteModalParams,
} from '../answer-template-delete-modal/answer-template-delete-modal.component';
import {
  AnswerTemplateManageModalCloseData,
  AnswerTemplateManageModalComponent,
  AnswerTemplateManageModalParams,
} from '../answer-template-manage-modal/answer-template-manage-modal.component';
// TODO: trazer esses adapters para dentro da lib
import {
  livechatTemplatesFilterCompanyAdapter,
  livechatTemplatesFilterCompanyChangeAdapter,
  livechatTemplatesManageAdapter,
  livechatTemplatesStartCreateAdapter,
  livechatTemplatesStartDeleteAdapter,
  livechatTemplatesStartEditAdapter,
} from '../../../../../../modules/live-chat/services/logger-adapters';
import { LiveChatStateService } from 'src/app/modules/live-chat/services/live-chat-state.service';
import { AttendanceChannel } from 'src/app/modules/live-chat/interfaces/attendance';
import { checkLimitChannelService } from '@core/services/check-limit-channel.service';
import { HandleKeyPressService, KeyPressEvent } from 'src/app/shared/services/handle-key-press/handle-key-press.service';

@Component({
  selector: 'app-answer-template-list',
  templateUrl: './answer-template-list.component.html',
  styleUrls: ['./answer-template-list.component.scss'],
})
export class AnswerTemplateListComponent implements OnInit {
  @ViewChild('templateList', { static: true })
  templateList!: ElementRef<HTMLDivElement>;
  @ViewChild(AskDropdownDirective) companyDropdown!: AskDropdownDirective;

  protected readonly DROPDOWN_POSITIONS: Array<ConnectedPosition> = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 4,
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetY: -4,
    },
  ];

  protected readonly URL_TEMPLATES_MANAGEMENT = `${environment.externalUrls.platform}/configurations/answer-templates`;

  public answerTemplates?: Array<AnswerTemplateDto>;
  processedTemplates?: Array<AnswerTemplateDto>;
  protected selectedIndex = 0;
  protected companies: Array<Company> = [];
  protected currentAttendanceCompany?: Company;
  protected selectedCompany?: Company | null = null;
  protected userCanAccessManageScreen = false;

  private destroyRef = inject(DestroyRef);
  private isModalOpen = false;
  protected readonly MAX_LENGTH_FACEBOOK_WEB_CHAT = 1600;
  protected readonly MAX_LENGTH_WHATSAPP = 3500;
  protected readonly MAX_LENGTH_INSTAGRAM = 1000;
  public exceededLimitAttendance = false;
  public channelAttendance? = AttendanceChannel.CHAT_WEB;
  

  constructor(
    @Inject(TRANSLATOR) private translate: Translator,
    private answerTemplateFacade: AnswerTemplatesFacade,
    private toaster: ToastService,
    private modal: ModalService,
    private logger: LoggerService,
    private liveChatStateService: LiveChatStateService,
    private checkLimitChannel: checkLimitChannelService,
    private handleKeyPressService: HandleKeyPressService
  ) {}

  ngOnInit() {
    this.checkUserRole();
    this.loadCompanies();
    this.listenCompanyFilter();
    this.listenTemplates();
    this.listenArrowsKeyPress();
    this.listenEnterKeyPress();
  }

  public selectCompany(company?: Company) {
    const attendanceId = this.answerTemplateFacade.getCurrentAttendanceId();
    if (attendanceId) {
      this.logger.log(
        LogEvent.LIVECHAT_TEMPLATES_FILTER_COMPANY_CHANGE,
        livechatTemplatesFilterCompanyChangeAdapter(
          attendanceId,
          this.selectedCompany?.companyId,
          company?.companyId
        )
      );
    }

    this.companyDropdown.close();
    this.answerTemplateFacade.filterByCompany(company?.companyId);
  }

  handleNewTemplateClick() {
    const template: Partial<AnswerTemplateDto> = {};

    if (this.currentAttendanceCompany?.companyId) {
      template.companiesIds = [this.currentAttendanceCompany.companyId];
    }

    this.openManagementModal({
      template,
      attendanceId: this.answerTemplateFacade.getCurrentAttendanceId() || '',
    });

    const attendanceId = this.answerTemplateFacade.getCurrentAttendanceId();
    attendanceId &&
      this.logger.log(
        LogEvent.LIVECHAT_TEMPLATES_START_CREATE,
        livechatTemplatesStartCreateAdapter(attendanceId)
      );
  }

  handleEditTemplateClick(event: Event, template: AnswerTemplateDto) {
    event.stopPropagation();
    this.openManagementModal({
      template,
      attendanceId: this.answerTemplateFacade.getCurrentAttendanceId() || '',
    });

    const attendanceId = this.answerTemplateFacade.getCurrentAttendanceId();
    if (attendanceId) {
      this.logger.log(
        LogEvent.LIVECHAT_TEMPLATES_START_EDIT,
        livechatTemplatesStartEditAdapter(
          attendanceId,
          template.idAnswerTemplate
        )
      );
    }
  }

  handleDeleteTemplateClick(event: Event, template: AnswerTemplateDto) {
    event.stopPropagation();
    this.openDeleteModal({
      template,
      attendanceId: this.answerTemplateFacade.getCurrentAttendanceId() || '',
    });

    const attendanceId = this.answerTemplateFacade.getCurrentAttendanceId();
    attendanceId &&
      this.logger.log(
        LogEvent.LIVECHAT_TEMPLATES_START_DELETE,
        livechatTemplatesStartDeleteAdapter(
          attendanceId,
          template.idAnswerTemplate
        )
      );
  }

  public openManagementModal(data?: AnswerTemplateManageModalParams) {
    this.modal
      .open(AnswerTemplateManageModalComponent, {
        data,
        hasBackdrop: true,
        onScroll: 'keep',
      })
      //@ts-ignore
      .afterClose.pipe(take(1))
      .subscribe((data: AnswerTemplateManageModalCloseData) => {
        this.isModalOpen = false;

        if (data?.shouldRefresh) {
          this.refreshList();
        }
      });
    this.isModalOpen = true;
  }

  public openDeleteModal(data: AnswerTemplateDeleteModalParams) {
    this.modal
      .open(AnswerTemplateDeleteModalComponent, {
        data,
        hasBackdrop: true,
        onScroll: 'keep',
      })
      //@ts-ignore
      .afterClose.pipe(take(1))
      .subscribe((data: AnswerTemplateDeleteModalCloseData) => {
        this.isModalOpen = false;

        if (data?.shouldRefresh) {
          this.refreshList();
        }
      });
    this.isModalOpen = true;
  }

  public refreshList() {
    this.answerTemplates = undefined;
    this.answerTemplateFacade.refreshList();
  }

  protected selectTemplate(template: AnswerTemplateDto) {
    this.answerTemplateFacade.applyTemplate(template);
  }

  protected focusOn(index: number) {
    this.selectedIndex = index;
  }

  protected answerTemplateIndex(
    index: number,
    template: AnswerTemplateDto
  ): number {
    return template.idAnswerTemplate;
  }

  protected logCompanyFilterOpening() {
    const attendanceId = this.answerTemplateFacade.getCurrentAttendanceId();

    if (!attendanceId) return;

    this.logger.log(
      LogEvent.LIVECHAT_TEMPLATES_FILTER_COMPANY,
      livechatTemplatesFilterCompanyAdapter(
        attendanceId,
        this.answerTemplateFacade.companyFilterValue
          ? 'current_company'
          : 'all_companies'
      )
    );
  }

  protected logManagementScreenClick() {
    const attendanceId = this.answerTemplateFacade.getCurrentAttendanceId();

    if (!attendanceId) return;

    this.logger.log(
      LogEvent.LIVECHAT_TEMPLATES_MANAGE,
      livechatTemplatesManageAdapter(attendanceId)
    );
    this.channelAttendance =
      this.liveChatStateService.currentAttendance?.currentContactChannel.channel;
  }

  private checkUserRole() {
    this.userCanAccessManageScreen =
      this.answerTemplateFacade.userCanAccessManageScreen();
  }

  private loadCompanies() {
    this.companies = this.answerTemplateFacade.getCompanies() || [];
    this.currentAttendanceCompany =
      this.answerTemplateFacade.getCurrentAttendanceCompany();
  }

  private listenCompanyFilter() {
    this.answerTemplateFacade.companyFilter$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((companyId) => {
        this.selectedCompany = this.companies.find(
          (c) => c.companyId === companyId
        );
      });
  }

  private listenTemplates() {
    this.answerTemplateFacade.answerTemplates$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: this.onTemplateLoad.bind(this),
        error: this.onTemplateLoadError.bind(this),
      });
  }

  private onTemplateLoad(templates?: Array<AnswerTemplateDto>) {
    if (!templates) return;
    this.answerTemplates = templates;
    this.selectedIndex = 0;
    this.processTemplates();
  }

  private onTemplateLoadError() {
    this.toaster.danger(this.translate.key('ERROR_LISTING_ANSWER_TEMPLATES'));
  }

  private listenArrowsKeyPress() {
      this.handleKeyPressService
        .getSpecificKeys(['ArrowUp', 'ArrowDown'])
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          filter(() => {
            const hasTemplate = (this.answerTemplates?.length ?? 0) > 0;
            const modalClosed = !this.isModalOpen;
            return hasTemplate && modalClosed;
          })
        )
        .subscribe((event) => {
          return this.handleArrowKeyPress(event);
        });
  }

  private listenEnterKeyPress() {
    this.handleKeyPressService
      .getSpecificKeys(['Enter'])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(() => {
          const hasTemplate = (this.answerTemplates?.length ?? 0) > 0;
          const modalClosed = !this.isModalOpen;
          return hasTemplate && modalClosed;
        })
      )
      .subscribe((event) => {
        return this.handleEnterPress(event);
      });
  }


  private handleArrowKeyPress(event: KeyPressEvent) { 
    const lastItemIndex =
      (this.answerTemplates as AnswerTemplateDto[]).length - 1;
    this.selectedIndex += event.key === 'ArrowUp' ? -1 : 1;

    if (this.selectedIndex < 0) this.selectedIndex = lastItemIndex;
    else if (this.selectedIndex > lastItemIndex) this.selectedIndex = 0;

    setTimeout(() => {
      const element =
        this.templateList.nativeElement.querySelectorAll('.template-item')[
          this.selectedIndex
        ];
      element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }

  private handleEnterPress(event: KeyPressEvent) {
    this.selectTemplate(
      this.answerTemplates?.[this.selectedIndex] as AnswerTemplateDto
    );
    event.preventDefault();
  }

  processTemplates(): void {
    this.processedTemplates = this.answerTemplates?.map((template) => {
      const content = template.content || '';
      template.exceededLimitAttendance =
        this.checkLimitChannel.calculateExceededLimit(
          content,
          this.channelAttendance
        );

      return {
        ...template,
      };
    });
  }
}
